import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { ref, onMounted, computed, reactive } from "vue";
import CardWrap from "@/components/common/CardWrap.vue";
import CardHeader from "./CardHeader.vue";
import CardInvoiceTable from "./CardInvoiceTable.vue";
import Loading from "@/components/common/Loading.vue";
import { ICompanyInvoice } from "@/models/company-invoice";
import { ICompanySetting } from "@/models/company-setting";
import { ISelectOption } from "@/models/select-option";
import { ISortDirection } from "@/models/sort-direction";
import companyInvoiceApi, { IGetCompanyInvoicesParams } from "@/api/company-invoice";
import companySettingApi, { IGetCompanySettingsParams } from "@/api/company-setting";
import companyInvoicesMonthApi from "@/api/company-invoices-month";
import store from "@/store";

interface IState {
  sortCondition: string;
  sortDirection: ISortDirection;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const isInitialLoading = ref(true);
const companyInvoices = ref<ICompanyInvoice[]>([]);
const companySettings = ref<ICompanySetting[]>([]);
const companyInvoicesMonth = ref<string[]>([]);
const selectedYearMonth = ref<string>("all");

onMounted(async () => {
  companyInvoicesMonthApi.getCompanyInvoicesMonths().then(async (res) => {
    companyInvoicesMonth.value = res;

    selectedYearMonth.value = store.state.date || companyInvoicesMonth.value[0];

    await getCompanyInvoicesOrSettings();

    isInitialLoading.value = false;
  });
});

const yearMonthList = computed((): ISelectOption[] => {
  const convertYearMonth = (yearMonth: string) => {
    const year = yearMonth.slice(0, 4);
    const month = yearMonth.slice(5, 7);
    return `${year}年${month}月`;
  };

  const convertedInvoicesMonth = companyInvoicesMonth.value.map((month) => {
    return {
      value: month,
      label: convertYearMonth(month),
    };
  });

  return [{ value: "all", label: "すべて" }, ...convertedInvoicesMonth];
});

const isLoading = ref(false);

const selectedYearMonthParams = computed((): IGetCompanyInvoicesParams | IGetCompanySettingsParams => {
  if (selectedYearMonth.value === "all") return {};

  const year = selectedYearMonth.value.slice(0, 4);
  const month = selectedYearMonth.value.slice(5, 7);
  return { year, month };
});

const getCompanyInvoices = async () => {
  // isLoading.value = true;
  companyInvoiceApi
    .getCompanyInvoices(selectedYearMonthParams.value)
    .then((res) => {
      companyInvoices.value = res;
      isLoading.value = false;
    });
};

const getCompanySettings = async () => {
  // isLoading.value = true;
  companySettingApi
    .getCompanySettings(selectedYearMonthParams.value)
    .then((res) => {
      companySettings.value = res;
      isLoading.value = false;
    });
};

const getCompanyInvoicesOrSettings = () => {
  if (selectedYearMonth.value === "all") {
    getCompanySettings();
  } else {
    getCompanyInvoices();
  }
};

const changeSelectedYearMonth = (e: Event) => {
  if (isLoading.value) return;

  isLoading.value = true;
  selectedYearMonth.value = (e.target as HTMLInputElement).value;

  store.commit('setDate', selectedYearMonth.value)

  getCompanyInvoicesOrSettings();
};

const escapeCsvField = (value: string | number) => {
  const stringValue = String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const exportCsv = () => {
  const rows = selectedYearMonth.value === "all" ? companySettings.value : companyInvoices.value;
  const headers = ["アカウント名", "月次契約数"];

  const csvLines = [
    headers.join(","),
    ...rows.map((row) => {
      return [
        escapeCsvField(row.company_name || ""),
        escapeCsvField(row.monthly_contract_count ?? ""),
      ].join(",");
    }),
  ];

  const csvContent = csvLines.join("\n");
  const blob = new Blob([`\uFEFF${csvContent}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `accounting_invoices_${selectedYearMonth.value}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const state = reactive<IState>({
  sortCondition: "id",
  sortDirection: "desc",
});

const changeSortCondition = (sortCondition: string) => {
  if (isLoading.value) return;

  if (state.sortCondition === sortCondition) {
    state.sortDirection = state.sortDirection === "desc" ? "asc" : "desc";
  } else {
    state.sortDirection = "desc";
  }
  state.sortCondition = sortCondition;

  isLoading.value = true;

  if (selectedYearMonth.value === "all") {
    let params: IGetCompanySettingsParams = {
      sortCondition: state.sortCondition,
      sortDirection: state.sortDirection,
    };

    companySettingApi.getCompanySettings(params).then((res) => {
      companySettings.value = res;
      isLoading.value = false;
    });
  } else {
    let params: IGetCompanyInvoicesParams = {
      sortCondition: state.sortCondition,
      sortDirection: state.sortDirection,
      ...selectedYearMonthParams.value,
    };

    companyInvoiceApi.getCompanyInvoices(params).then((res) => {
      companyInvoices.value = res;
      isLoading.value = false;
    });
  }

};

return (_ctx: any,_cache: any) => {
  return (isInitialLoading.value)
    ? (_openBlock(), _createBlock(Loading, {
        key: 0,
        "is-loading": isInitialLoading.value,
        "is-fixed": ""
      }, null, 8, ["is-loading"]))
    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
        (isLoading.value)
          ? (_openBlock(), _createBlock(Loading, {
              key: 0,
              "is-loading": isLoading.value,
              "is-fixed": ""
            }, null, 8, ["is-loading"]))
          : _createCommentVNode("", true),
        _createVNode(CardHeader, {
          class: "mb-gap",
          "selected-year-month": selectedYearMonth.value,
          "year-month-list": yearMonthList.value,
          "change-selected-year-month": changeSelectedYearMonth,
          "export-csv": exportCsv
        }, null, 8, ["selected-year-month", "year-month-list"]),
        _createVNode(CardWrap, null, {
          default: _withCtx(() => [
            (selectedYearMonth.value !== 'all')
              ? (_openBlock(), _createBlock(CardInvoiceTable, {
                  key: 0,
                  "is-selected-all": false,
                  "table-data": companyInvoices.value,
                  "onUpdate:tableData": _cache[0] || (_cache[0] = ($event: any) => ((companyInvoices).value = $event)),
                  "sort-condition": state.sortCondition,
                  "sort-direction": state.sortDirection,
                  "change-sort-condition": changeSortCondition
                }, null, 8, ["table-data", "sort-condition", "sort-direction"]))
              : _createCommentVNode("", true),
            (selectedYearMonth.value === 'all')
              ? (_openBlock(), _createBlock(CardInvoiceTable, {
                  key: 1,
                  "is-selected-all": true,
                  "table-data": companySettings.value,
                  "sort-condition": state.sortCondition,
                  "sort-direction": state.sortDirection,
                  "change-sort-condition": changeSortCondition
                }, null, 8, ["table-data", "sort-condition", "sort-direction"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ], 64))
}
}

})